import React, { useState, useCallback, useMemo } from 'react';
import DataGrid from '../../common/DataGrid';
import ViewToolbar from '../../common/ViewToolbar';
import { usePeriodFilter } from '../../../hooks/usePeriodFilter';
import {
  fetchSalesByItemSummary,
  fetchSalesByItemDetail,
} from '../../../services/reportService';

const currencyFormatter = (params) => {
  if (params.value == null) return '';
  return Number(params.value).toFixed(2);
};

const percentFormatter = (params) => {
  if (params.value == null) return '';
  return `${Number(params.value).toFixed(2)}%`;
};

const qtyFormatter = (params) => {
  if (params.value == null) return '';
  return Number(params.value).toLocaleString();
};

const dateFormatter = (params) => {
  if (!params.value) return '';
  return new Date(params.value).toLocaleDateString();
};

const summaryColumns = [
  { field: 'item_name', headerName: 'Item', flex: 2, minWidth: 200 },
  { field: 'total_qty', headerName: 'Qty Sold', valueFormatter: qtyFormatter, type: 'rightAligned', minWidth: 100, flex: 0.7 },
  { field: 'total_sales_excl', headerName: 'Sales (Excl)', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 120, flex: 1 },
  { field: 'total_tax', headerName: 'Tax', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 100, flex: 0.8 },
  { field: 'total_sales_incl', headerName: 'Sales (Incl)', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 120, flex: 1 },
  { field: 'total_cogs', headerName: 'COGS', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 110, flex: 1 },
  { field: 'gross_profit', headerName: 'Gross Profit', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 120, flex: 1 },
  { field: 'gp_pct', headerName: 'GP %', valueFormatter: percentFormatter, type: 'rightAligned', minWidth: 90, flex: 0.7 },
];

const detailColumns = [
  { field: 'invoice_date', headerName: 'Date', minWidth: 110, flex: 0.8, valueFormatter: dateFormatter },
  { field: 'invoice_number', headerName: 'Invoice #', minWidth: 100, flex: 0.7 },
  { field: 'customer_name', headerName: 'Customer', minWidth: 150, flex: 1.2 },
  { field: 'qty', headerName: 'Qty', valueFormatter: qtyFormatter, type: 'rightAligned', minWidth: 70, flex: 0.5 },
  { field: 'unit_price', headerName: 'Unit Price', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 100, flex: 0.8 },
  { field: 'line_total_excl', headerName: 'Total (Excl)', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 110, flex: 1 },
  { field: 'line_tax', headerName: 'Tax', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 90, flex: 0.7 },
  { field: 'line_total_incl', headerName: 'Total (Incl)', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 110, flex: 1 },
  { field: 'line_cogs', headerName: 'COGS', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 100, flex: 0.8 },
  { field: 'line_gp', headerName: 'GP', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 100, flex: 0.8 },
  { field: 'line_gp_pct', headerName: 'GP %', valueFormatter: percentFormatter, type: 'rightAligned', minWidth: 80, flex: 0.6 },
];

export default function SalesByItemView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gridApi, setGridApi] = useState(null);

  const [selectedItem, setSelectedItem] = useState(null);
  const [detailRows, setDetailRows] = useState([]);

  const periodFilter = usePeriodFilter('this_month');

  const loadSummary = useCallback(async () => {
    const { dateFrom, dateTo } = periodFilter;
    if (!dateFrom || !dateTo) return;

    try {
      setLoading(true);
      setSelectedItem(null);
      setDetailRows([]);
      const data = await fetchSalesByItemSummary(dateFrom, dateTo);
      setRows(data);
    } catch (err) {
      console.error('Error fetching sales by item:', err);
    } finally {
      setLoading(false);
    }
  }, [periodFilter]);

  const handleItemClick = useCallback(async (row) => {
    const { dateFrom, dateTo } = periodFilter;
    if (!dateFrom || !dateTo) return;

    try {
      setLoading(true);
      setSelectedItem(row);
      const data = await fetchSalesByItemDetail(dateFrom, dateTo, row.item_id);
      setDetailRows(data);
    } catch (err) {
      console.error('Error fetching item detail:', err);
    } finally {
      setLoading(false);
    }
  }, [periodFilter]);

  const handleBack = useCallback(() => {
    setSelectedItem(null);
    setDetailRows([]);
  }, []);

  const summaryTotals = useMemo(() => {
    if (!rows.length) return null;
    const totalQty = rows.reduce((s, r) => s + Number(r.total_qty || 0), 0);
    const totalSalesExcl = rows.reduce((s, r) => s + Number(r.total_sales_excl || 0), 0);
    const totalTax = rows.reduce((s, r) => s + Number(r.total_tax || 0), 0);
    const totalSalesIncl = rows.reduce((s, r) => s + Number(r.total_sales_incl || 0), 0);
    const totalCogs = rows.reduce((s, r) => s + Number(r.total_cogs || 0), 0);
    const grossProfit = totalSalesExcl - totalCogs;
    const gpPct = totalSalesExcl === 0 ? 0 : ((grossProfit / totalSalesExcl) * 100);
    return { totalQty, totalSalesExcl, totalTax, totalSalesIncl, totalCogs, grossProfit, gpPct };
  }, [rows]);

  const detailTotals = useMemo(() => {
    if (!detailRows.length) return null;
    const totalQty = detailRows.reduce((s, r) => s + Number(r.qty || 0), 0);
    const totalSalesExcl = detailRows.reduce((s, r) => s + Number(r.line_total_excl || 0), 0);
    const totalTax = detailRows.reduce((s, r) => s + Number(r.line_tax || 0), 0);
    const totalSalesIncl = detailRows.reduce((s, r) => s + Number(r.line_total_incl || 0), 0);
    const totalCogs = detailRows.reduce((s, r) => s + Number(r.line_cogs || 0), 0);
    const grossProfit = totalSalesExcl - totalCogs;
    const gpPct = totalSalesExcl === 0 ? 0 : ((grossProfit / totalSalesExcl) * 100);
    return { totalQty, totalSalesExcl, totalTax, totalSalesIncl, totalCogs, grossProfit, gpPct };
  }, [detailRows]);

  const activeTotals = selectedItem ? detailTotals : summaryTotals;

  return (
    <div className="flex flex-col h-full">
      <div className="mb-4 flex flex-wrap gap-3 items-end">
        <ViewToolbar
          searchTerm=""
          onSearchChange={() => {}}
          placeholder="Search..."
          addLabel="Run Report"
          addColor="bg-blue-600 hover:bg-blue-700"
          onAddNew={loadSummary}
          onExportCsv={gridApi ? () => gridApi.exportToCsv() : undefined}
          period={periodFilter.period}
          onPeriodChange={periodFilter.onPeriodChange}
          dateFrom={periodFilter.dateFrom}
          onDateFromChange={periodFilter.onDateFromChange}
          dateTo={periodFilter.dateTo}
          onDateToChange={periodFilter.onDateToChange}
        />
      </div>

      {selectedItem && (
        <div className="mb-3 flex items-center gap-2 text-sm">
          <button
            onClick={handleBack}
            className="text-blue-600 hover:text-blue-800 font-medium cursor-pointer flex items-center gap-1"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            All Items
          </button>
          <span className="text-gray-400">/</span>
          <span className="font-semibold text-gray-700">{selectedItem.item_name}</span>
        </div>
      )}

      {activeTotals && (
        <div className="mb-3 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          <TotalCard label="Qty Sold" value={activeTotals.totalQty} isCurrency={false} isInteger />
          <TotalCard label="Sales (Excl)" value={activeTotals.totalSalesExcl} />
          <TotalCard label="Tax" value={activeTotals.totalTax} />
          <TotalCard label="Sales (Incl)" value={activeTotals.totalSalesIncl} />
          <TotalCard label="COGS" value={activeTotals.totalCogs} />
          <TotalCard label="Gross Profit" value={activeTotals.grossProfit} />
          <TotalCard label="GP %" value={activeTotals.gpPct} suffix="%" isCurrency={false} />
        </div>
      )}

      {loading ? (
        <div className="flex justify-center mt-10 text-gray-500">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="flex justify-center mt-10 text-gray-400 text-sm">
          Select a period and click "Run Report" to view sales data.
        </div>
      ) : (
        <div className="flex-1 min-h-0">
          {selectedItem ? (
            <DataGrid
              rowData={detailRows}
              columns={detailColumns}
              onGridReady={setGridApi}
              exportFileName="sales-by-item-detail.csv"
              pagination
              paginationSize={20}
            />
          ) : (
            <DataGrid
              rowData={rows}
              columns={summaryColumns}
              onEdit={handleItemClick}
              onGridReady={setGridApi}
              exportFileName="sales-by-item.csv"
              editLabel="View"
              pagination
              paginationSize={20}
            />
          )}
        </div>
      )}
    </div>
  );
}

function TotalCard({ label, value, suffix, isCurrency = true, isInteger = false }) {
  let formatted;
  if (isInteger) {
    formatted = Number(value || 0).toLocaleString();
  } else if (isCurrency) {
    formatted = Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } else {
    formatted = Number(value || 0).toFixed(2);
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 shadow-sm">
      <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-lg font-semibold text-gray-800 mt-0.5">
        {isCurrency && '$'}{formatted}{suffix || ''}
      </div>
    </div>
  );
}
